<rt-dialog-header [heading]="heading" [subheading]="subheading" />

<rt-dialog-body>
  <rt-group variant="vertical">
    <rt-box variant="vertical background no-padding">
      @if (isSearchEnabled) {
        <div class="search">
          <rt-form-field [class.small-padding-bottom]="!isHeaderHidden && !isEmpty()">
            <rt-input
              variant="rounded"
              placeholder="{{ 'app.common.placeholders.search.offers_search.other' | translate }}"
              [autoFocus]="true"
              [formControl]="searchControl"
              [hasClearButton]="true"
              (reset)="cancelSearch()"
            >
              <rt-icon prefix name="search" />
            </rt-input>
          </rt-form-field>
        </div>
      }

      <div cdkDropList (cdkDropListDropped)="onDrop($event)" class="groups">
        @for (
          group of state.groupedOffersByKind;
          track trackByGroupTitle($index, group);
          let first = $first;
          let last = $last
        ) {
          <div class="group" layout-vertical>
            @if (!offersGroupEmpty(group) && !isHeaderHidden) {
              <div layout layout-space-between layout-align-items-center class="header-container">
                <rt-heading variant="heading uppercased">
                  {{ group.title }}
                </rt-heading>
                @if (group.sortable) {
                  <rt-button
                    (click)="sortDropdown.open = !sortDropdown.open"
                    #sortDropdownOrigin="rtOverlayOrigin"
                    rt-overlay-origin
                    [dropdownOpened]="sortDropdown.open"
                    variant="plain"
                    padding-right-small-x
                  >
                    <rt-icon name="sort" margin-right-small-xx />
                    <span>{{ sortButtonLabel }}</span>
                  </rt-button>
                  <ng-template
                    #sortDropdown="rtConnectedOverlay"
                    [origin]="sortDropdownOrigin"
                    (backdropClick)="sortDropdown.open = false"
                    rt-connected-overlay
                    hasBackdrop
                  >
                    <rt-dropdown [width]="DropdownWidth.SmallX">
                      <rt-dropdown-header [fullWidthTitle]="true">
                        {{ 'app.offers.sort_by.dropdown_title' | translate }}
                      </rt-dropdown-header>
                      @for (
                        sortOption of sortOptionsToArray;
                        track trackByValue($index, sortOption)
                      ) {
                        <rt-dropdown-item (click)="changeSortOption(sortOption)">
                          {{ getSortLabelTranslation(sortOption) }}
                        </rt-dropdown-item>
                      }
                    </rt-dropdown>
                  </ng-template>
                }
              </div>
            }
            <div class="results">
              @for (
                filteredGroup of group.groups;
                track trackByTemplateGroup($index, filteredGroup)
              ) {
                <div class="result-group">
                  <ng-template
                    #elementInjector="elementInjector"
                    elementInjector
                    [ngTemplateOutlet]="getTemplate(filteredGroup.templateKind)"
                    [ngTemplateOutletContext]="{ $implicit: filteredGroup }"
                    [ngTemplateOutletInjector]="elementInjector.injector"
                  />
                </div>
              }
            </div>
          </div>
        }
        @if (searchControl.value !== '' && isEmpty()) {
          <rt-empty-state
            variant="small"
            [icon]="'error-outline'"
            [title]="'app.common.no_results_found' | translate"
          >
            <rt-button variant="tertiary" (click)="cancelSearch()">
              {{ 'app.assignments.editor.clear_search' | translate }}
            </rt-button>
          </rt-empty-state>
        }
      </div>
    </rt-box>
    @if (data && data.additionalInfobox && isSelectedOfferWithAssignedHires) {
      <rt-infobox
        [iconName]="data.additionalInfobox.iconName"
        [type]="data.additionalInfobox.type"
        (dismissClick)="dismissInfobox()"
        margin-top-medium
      >
        <span [innerHTML]="data.additionalInfobox.copyTranslationKey | translate"></span>
      </rt-infobox>
    }
  </rt-group>
</rt-dialog-body>

<rt-dialog-footer [hasOptionalActions]="hasGoBack">
  @if (hasGoBack) {
    <rt-button class="optional" (click)="goBack()" variant="plain">
      <rt-icon name="arrow-back" />
      {{ 'app.common.go_back' | translate }}
    </rt-button>
  }
  @if (!isLoading) {
    <rt-button variant="plain" (click)="cancel()" margin-right-small>
      {{ 'app.common.cancel' | translate }}
    </rt-button>
  }
  <rt-button variant="primary" [loading]="isLoading" [disabled]="isDisabled()" (click)="submit()">
    {{ submitLabel }}
  </rt-button>
</rt-dialog-footer>

<ng-template #defaultTpl let-filteredGroup>
  @for (offer of filteredGroup.items; track trackByOffer($index, offer)) {
    <div
      cdkDrag
      cdkDragBoundary=".groups"
      [cdkDragDisabled]="selectMode !== 'reorder'"
      [class.has-background]="selectMode === 'reorder'"
      class="result"
    >
      <ng-container [ngTemplateOutlet]="itemTpl" [ngTemplateOutletContext]="{ offer }" />
      <rt-divider />
    </div>
  }
</ng-template>

<ng-template #groupTpl let-filteredGroup>
  <div class="filtered-group-header" layout layout-align-items-center>
    <rt-heading variant="subheading" margin-right-small-xx>
      @if (state.activeSort === SortOptions.Status) {
        {{ getJobStatusTranslation(filteredGroup.groupTitle) }}
      } @else {
        {{ filteredGroup.groupTitle }}
      }
      <ng-template #groupTitle>
        {{ filteredGroup.groupTitle }}
      </ng-template>
    </rt-heading>
    <span>({{ filteredGroup.items.length }})</span>
  </div>
  <rt-divider />
  @for (offer of filteredGroup.items; track trackByOffer($index, offer)) {
    <div class="result">
      <ng-container [ngTemplateOutlet]="itemTpl" [ngTemplateOutletContext]="{ offer }" />
      <rt-divider />
    </div>
  }
</ng-template>

<ng-template #itemTpl let-offer="offer">
  <rt-group
    variant="horizontal"
    layout-align-items-center
    [class.checked]="isSelected(offer)"
    (click)="toggleAssignment(offer)"
  >
    @if (selectMode === 'single') {
      <rt-radio [checked]="isSelected(offer)" (click)="toggleAssignment(offer)" />
    }
    @if (selectMode === 'multi') {
      <rt-checkbox [checked]="isSelected(offer)" (click)="toggleAssignment(offer)" />
    }
    @if (selectMode === 'reorder') {
      <rt-icon name="drag-vertical" margin-right-small />
    }
    <rt-offer-status-indicator [offerStatus]="offer" />
    <div class="offer-content">
      <div class="title">
        <span padding-right-small-xx>
          {{ offer.title }}
        </span>
        <span><rt-badge [text]="'#' + offer.guid" color="secondary" class="transform-none" /></span>
      </div>

      @if (offer.department || offer.locationIds?.length || offer.location) {
        <div layout class="description">
          @if (offer.department) {
            <span
              #departmentRef
              [rtTooltip]="offer.department"
              [rtTooltipEnabled]="departmentTooltipEnabledMap[offer.id]"
              [rtTooltipPosition]="'below'"
              [class.restricted]="offer.locationIds?.length || offer.location"
              [attr.data-offer-id]="offer.id"
              class="department"
            >
              {{ offer.department }}
            </span>
          }
          @if (offer.locationIds?.length || offer.location) {
            @if (offer.department) {
              <rt-divider variant="dot" />
            }
            @if (offer.locationIds?.length) {
              <rt-assigned-locations-info-container [locationIds]="offer.locationIds" />
            } @else {
              <span>{{ offer.location }}</span>
            }
          }
        </div>
      }
    </div>
  </rt-group>
</ng-template>
